import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MagazineAdapter } from '@modules/content-api/adapter/magazine.adapter';
import { ContentAPIService } from '@modules/content-api/content-api.service';
import { UserService } from '@modules/shared/services/user.service';
import { combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

export const MAGAZINE_PAGE_PARAMS = new HttpParams().appendAll({
  include: [
    'sections',
    'sections.contacts.image.image',
    'sections.contacts.image.adjusted_image',
    'sections.cards.stream.background',
    'sections.cards.stream.player_config',
    'sections.cards.stream.player_config.transcript',
    'sections.cards.media.image',
    'sections.cards.media.adjusted_image',
    'sections.cards.media.video',
    'sections.cards.media.poster_image',
    'sections.cards.media.alternate_images',
    'sections.cards.presskit',
    'sections.announcement_banner.announcements',
    'sections.announcement_banner.background.alternate_images',
    'sections.announcement_banner.background.image',
    'sections.announcement_banner.background.adjusted_image',
    'sections.announcement_banner.background.video',
    'sections.announcement_banner.buttons',
    'sections.announcement_banner.streams.stream.player_config',
    'sections.announcement_banner.streams.stream.background',
    'sections.announcement_banner.streams.page_card',
    'sections.qa_form.selectable_contact_people',
    'sections.tag_filters',
    'sections.topic_tree',
  ].join(),
});

const EVENT_PAGE_PARAMS = new HttpParams().appendAll({
  include: [
    'event',
    'sections.cards.stream.background',
    'sections.cards.stream.player_config',
    'sections.cards.stream.player_config.transcript',
    'sections.cards.media.image',
    'sections.cards.media.adjusted_image',
    'sections.cards.media.video',
    'sections.cards.media.poster_image',
    'sections.cards.media.alternate_images',
    'sections.cards.presskit',
    'sections.test_routes',
    'sections.test_cars.image',
    'sections.test_cars.document.file',
    'sections.preview_map_large',
    'sections.preview_map_small',
    'sections.agenda_items',
    'sections.highlight.image',
    'sections.announcement_banner.announcements',
    'sections.announcement_banner.background.alternate_images',
    'sections.announcement_banner.background.image',
    'sections.announcement_banner.background.adjusted_image',
    'sections.announcement_banner.background.video',
    'sections.announcement_banner.buttons',
    'sections.announcement_banner.streams.stream.player_config',
    'sections.announcement_banner.streams.stream.background',
    'sections.announcement_banner.streams.page_card',
  ].join(),
  'fields[test_route]': 'day_number',
  'fields[event]': ['ermv3_id', 'erm_id'].join(),
});

@Injectable({
  providedIn: 'root',
})
export class MagazinePageService {
  constructor(
    private apiService: ContentAPIService,
    private magazineAdapter: MagazineAdapter,
    private userService: UserService
  ) {}

  public getMagazine(slug: string) {
    const pageRequests = [
      this.apiService.getResources('pages', MAGAZINE_PAGE_PARAMS.append('filter[slug.value]', `/page/${slug}`)).pipe(
        map((data) => {
          return {
            page: data.length ? data[0] : null,
            status: data.length ? 200 : 404,
          };
        })
      ),
    ];

    return this.userService.isLoggedIn().pipe(
      take(1),
      switchMap((user) => {
        if (user) {
          // only request event_pages for logged in users
          pageRequests.push(
            this.apiService
              .getResources('event_pages', EVENT_PAGE_PARAMS.append('filter[slug.value]', `/event-page/${slug}`))
              .pipe(
                map((data) => {
                  return {
                    page: data.length ? data[0] : null,
                    status: data.length ? 200 : 404,
                  };
                }),
                catchError((error) => {
                  switch (error.status) {
                    case 401:
                      return of({
                        page: null,
                        status: 401,
                      });
                    case 403:
                      return of({
                        page: null,
                        status: 403,
                      });
                    default:
                      throw error;
                  }
                })
              )
          );
        }

        return combineLatest(pageRequests).pipe(
          take(1),
          map(([magazine, event]) => {
            // if the user is not logged-in, we do not even try to request the (uncached) event page
            if (!event) {
              if (magazine.page) {
                return magazine.page;
              } else {
                // IMPROVE: Could be a 404 as well (we do not know, without doing the uncached request)
                //          Easy solution would be to drop the requirement of service two page types under the same slug
                throw new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' });
              }
            }

            // If there is no event page, we have to analyse the meta information
            // and check if an existing event page was omitted due to authentation or authorization
            if (!event.page) {
              switch (event.status) {
                case 401: {
                  // NOTE: this will almost never happen, as we do not request event pages for anonymous users
                  if (magazine.page) {
                    // appending an error section to the magazine page
                    magazine.page.sections.push({
                      customHeadline: true,
                      type: 'info',
                      infoType: 'primary',
                      title: 'EVENT_PAGE_AUTH_INFO.UNAUTHORIZED.TITLE',
                      text: 'EVENT_PAGE_AUTH_INFO.UNAUTHORIZED.TEXT',
                    });
                  } else {
                    throw new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' });
                  }
                  break;
                }
                case 403: {
                  if (magazine.page) {
                    // appending an error section to the magazine page
                    magazine.page.sections.push({
                      customHeadline: true,
                      type: 'info',
                      infoType: 'primary',
                      title: 'EVENT_PAGE_AUTH_INFO.FORBIDDEN.TITLE',
                      text: 'EVENT_PAGE_AUTH_INFO.FORBIDDEN.TEXT',
                    });
                  } else {
                    throw new HttpErrorResponse({ status: 403, statusText: 'Forbidden' });
                  }
                  break;
                }
                case 404: {
                  if (!magazine.page) {
                    throw new HttpErrorResponse({ status: 404, statusText: 'Not found' });
                  }
                  break;
                }
              }

              return magazine.page || null;
            }

            // combine sections of both pages
            let data: any = magazine.page || event.page;
            if (data) {
              // Event page title takes precedence
              data.title = (event.page || magazine.page).title;
              data.sections = [].concat(...[magazine.page?.sections, event.page?.sections].filter((page) => !!page));

              const CARLEventId = event.page?.event?.ermv3_id;

              // If the event page has a CARL event connected
              if (!!CARLEventId) {
                // we append an event-registration-section to the end
                data.sections.push({
                  type: 'event_registration_section',
                  eventId: CARLEventId,
                });

                // the ERM based sections (Experts/Contacts) need to Event Id to fetch the data
                data.sections.map((section: any) => {
                  if (section.type === 'event_contacts_section' || section.type === 'event_experts_section') {
                    section['eventId'] = CARLEventId;
                  }
                  return section;
                });
              }

              return data;
            }
            return null;
          }),
          map((data) => {
            try {
              return this.magazineAdapter.parse(data);
            } catch (error) {
              // Additional error handling for parse errors?
              return null;
            }
          })
        );
      })
    );
  }
}
